import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Alert,
  useTheme,
  useMediaQuery,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { createStylist, getStylists } from '@services/stylistService';
import { updateStylistDetails, getStylistDetails } from '@services/stylistService';
import { createPlan, getServiceTypes, getPlans } from '@services/planService';
import { getSubServiceTypes } from '@services/subServiceTypeService';
import { getAreaLocations } from '@services/areaService';
import { updateStaffStatus, updateBusinessStatus, updateUserFlag } from '@services/profileService';
import { toast } from 'react-toastify';
import StylistCreationStep from './quickListing/StylistCreationStep';
import StylistProfileStep from './quickListing/StylistProfileStep';
import PlanCreationStep from './quickListing/PlanCreationStep';
import ShowcaseStep from './quickListing/ShowcaseStep';
import AcknowledgmentStep from './quickListing/AcknowledgmentStep';
import GoogleMapsLinks from './common/GoogleMapsLinks';

interface AreaLocation {
  _id: string;
  name?: string;
  zh_name?: string;
  location_code?: string;
  chinese?: string;
  english?: string;
  [key: string]: unknown;
}

interface SubServiceType {
  _id: string;
  name: string;
  zh_name: string;
}

interface ServiceType {
  _id: string;
  name: string;
  [key: string]: unknown;
}

type FormDataValue = string | number | boolean | File | Blob | null | undefined | FormDataValue[] | { [key: string]: FormDataValue };

interface PlaceData {
  address: string;
  coordinates: [number, number] | null;
  place_id: string;
}

interface ExistingPlan {
  _id: string;
  name?: string;
  zh_name?: string;
  price?: string;
  duration?: string;
  description?: string;
  zh_description?: string;
  serviceTypeId?: string;
  subServiceTypeId?: string;
  [key: string]: unknown;
}

interface QuickListingWizardProps {
  open: boolean;
  onClose: () => void;
  fetchUserData: () => Promise<void> | void;
  initialStep?: number;
  initialStylistId?: string | null;
}

const QuickListingWizard: React.FC<QuickListingWizardProps> = ({ open, onClose, fetchUserData, initialStep = 0, initialStylistId = null }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [stylistId, setStylistId] = useState<string | null>(initialStylistId || null);

  // Step 1: Stylist Creation
  const [useBusinessEmail, setUseBusinessEmail] = useState<boolean>(true);
  const [stylistEmail, setStylistEmail] = useState<string>('');

  // Step 2: Stylist Profile
  const [displayName, setDisplayName] = useState<string>('');
  const [yearOfExperience, setYearOfExperience] = useState<number>(3);
  const [canCommunicateInEnglish, setCanCommunicateInEnglish] = useState<boolean>(false);
  const [description, setDescription] = useState<string>('');
  const [zh_description, setZhDescription] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [dayOff, setDayOff] = useState<number[]>([1]); // Default to Monday
  const [startHour, setStartHour] = useState<string>('09:00');
  const [endHour, setEndHour] = useState<string>('18:00');
  const [subServiceTypes, setSubServiceTypes] = useState<SubServiceType[]>([]);
  const [subServiceTypesOptions, setSubServiceTypesOptions] = useState<SubServiceType[]>([]);
  const [useBusinessAddress, setUseBusinessAddress] = useState<boolean>(true);
  const [businessAddress, setBusinessAddress] = useState<string>('');
  const [businessArea, setBusinessArea] = useState<AreaLocation | null>(null);
  const [areaLocations, setAreaLocations] = useState<AreaLocation[]>([]);
  const [coordinates, setCoordinates] = useState<number[]>([]);
  const [placeId, setPlaceId] = useState<string>('');

  // Step 3: Plan Creation
  const [planName, setPlanName] = useState<string>('');
  const [planZhName, setPlanZhName] = useState<string>('');
  const [planPrice, setPlanPrice] = useState<string>('');
  const [planDuration, setPlanDuration] = useState<string>('');
  const [planDescription, setPlanDescription] = useState<string>('');
  const [planZhDescription, setPlanZhDescription] = useState<string>('');
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState<string>('');
  const [selectedSubServiceTypeId, setSelectedSubServiceTypeId] = useState<string>('');
  const [existingPlans, setExistingPlans] = useState<ExistingPlan[]>([]);

  const [showcaseCount, setShowcaseCount] = useState<number>(0);

  // New state for Reservation Mode
  const [reservationMode, setReservationMode] = useState<string>('auto-confirm');

  // New state for Accept Platform Coupons
  const [isAcceptPlatformCoupons, setIsAcceptPlatformCoupons] = useState<boolean>(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}') as Record<string, unknown>;
  const isStylistRole = user?.role === 'stylist';

  // At the beginning of the component, add a render counter
  const renderCount = useRef<number>(0);

  // Helper function to clean up session storage and URL parameters
  const cleanupWizardState = (): void => {
    // Clean up session storage items
    sessionStorage.removeItem('wizard_open');
    sessionStorage.removeItem('wizard_step');
    sessionStorage.removeItem('wizard_stylist_id');

    // Clean up URL parameters if present
    const url = new URL(window.location.href);
    if (url.searchParams.has('wizard') || url.searchParams.has('step') || url.searchParams.has('instagram_success')) {
      url.searchParams.delete('wizard');
      url.searchParams.delete('step');
      url.searchParams.delete('instagram_success');
      window.history.replaceState({}, '', url.toString());
      console.log('QuickListingWizard: Cleaned up URL parameters and session storage');
    }
  };

  // Log when the component renders
  useEffect(() => {
    renderCount.current += 1;
    console.log('Component rendered, render count:', renderCount.current);
  });

  useEffect(() => {
    console.log('activeStep changed to:', activeStep);
  }, [activeStep]);

  // Effect to sync activeStep with initialStep prop, primarily for when wizard is controlled externally
  useEffect(() => {
    console.log('QuickListingWizard: initialStep prop changed to:', initialStep);
    setActiveStep(initialStep);
  }, [initialStep]);

  // Effect to sync internal stylistId with initialStylistId prop
  useEffect(() => {
    console.log('QuickListingWizard: initialStylistId prop changed to:', initialStylistId);
    if (initialStylistId) {
        setStylistId(initialStylistId);
    }
    // If the wizard is not open and initialStylistId becomes null (e.g., wizard closed from Dashboard),
    // ensure local stylistId is also cleared for next fresh open.
    else if (!open && !initialStylistId) {
        setStylistId(null);
    }
  }, [initialStylistId, open]);

  // Effect to reset wizard state when it's freshly opened (not restored)
  // or to load stylist data if stylistId is present upon opening.
  useEffect(() => {
    if (open) {
      console.log('QuickListingWizard: Wizard is open. Initial Step:', initialStep, 'Current StylistId:', stylistId, 'Prop initialStylistId:', initialStylistId);
      // Always fetch service types when wizard opens
      fetchServiceTypes();
      fetchSubServiceTypes();
      fetchAreaLocations();

      if (isStylistRole && !initialStylistId) {
        console.log('QuickListingWizard: Stylist user detected, skipping to step 2.');
        setStylistId(user._id as string);
        setActiveStep(1); // Stylists start at step 2 (index 1)
        return;
      }

      // If initialStep is 0 (a fresh start) and no initialStylistId is provided, perform a full reset.
      if (initialStep === 0 && !initialStylistId) {
        console.log('QuickListingWizard: Full reset for fresh open (initialStep 0, no initialStylistId).');
        setActiveStep(0);
        setError('');
        setSuccess('');
        setStylistId(null); // Crucial for fresh start
        // Reset all form-specific states
        setUseBusinessEmail(true);
        setStylistEmail('');
        setDisplayName('');
        setYearOfExperience(3);
        setCanCommunicateInEnglish(false);
        setDescription('');
        setZhDescription('');
        setAvatarFile(null);
        setPreviewUrl('');
        setDayOff([1]);
        setStartHour('09:00');
        setEndHour('18:00');
        setSubServiceTypes([]);
        setUseBusinessAddress(true);
        setBusinessAddress('');
        setBusinessArea(null);
        setPlanName('');
        setPlanZhName('');
        setPlanPrice('');
        setPlanDuration('');
        setPlanDescription('');
        setPlanZhDescription('');
        setSelectedServiceTypeId('');
        setSelectedSubServiceTypeId('');
        setExistingPlans([]);
        setReservationMode('need-approval');
        setIsAcceptPlatformCoupons(false);
        setShowcaseCount(0);
      }
      // If initialStylistId is provided and internal stylistId is not yet set or different,
      // it's handled by the useEffect for [initialStylistId].
      // Data fetching for stylistId is handled in a separate useEffect.

    } else {
      // Optionally, when wizard is closed, clear any lingering sessionStorage items specific to wizard internal state if any were set by it.
      // However, Dashboard now clears the main ones.
      console.log('QuickListingWizard: Wizard is closed.');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStep, initialStylistId]); // Dependencies ensure this runs when these key props change or wizard opens/closes.

  // Effect to fetch stylist details and their plans when stylistId is available and wizard is open
  useEffect(() => {
    const fetchStylistData = async (): Promise<void> => {
      if (stylistId) {
        console.log('QuickListingWizard: Fetching data for stylistId:', stylistId);
        setIsLoading(true);
        try {
          const detailsResponse = await getStylistDetails(stylistId);
          const details = detailsResponse;
          if (details) {
            setDisplayName(details.displayName || '');
            setYearOfExperience((details.yearOfExperience as number) || 3);
            setCanCommunicateInEnglish((details.canCommunicateInEnglish as boolean) || false);
            setDescription((details.description as string) || '');
            setZhDescription((details.zh_description as string) || '');
            setUseBusinessAddress((details.useBusinessAddress as boolean) ?? true);
            setBusinessAddress((details.businessAddress as string) || '');
            setBusinessArea((details.businessArea as AreaLocation) || null);
            setCoordinates((details.coordinates as number[]) || []);

            const hydratedSubServiceTypes = ((details.subServiceTypes as (SubServiceType | string)[]) || [])
              .map((subService: SubServiceType | string) => {
                const idToFind = typeof subService === 'string' ? subService : subService._id;
                return subServiceTypesOptions.find(option => option._id === idToFind);
              })
              .filter(Boolean) as SubServiceType[];
            setSubServiceTypes(hydratedSubServiceTypes);

            if (details.availability) {
              setDayOff(details.availability.dayoff || [1]);
              setStartHour(details.availability.startHour || '09:00');
              setEndHour(details.availability.endHour || '18:00');
            }
            setPreviewUrl(details.avatarKey ? `${import.meta.env.VITE_R2_SALON_ENDPOINT}/${details.avatarKey}` : '');
            setIsAcceptPlatformCoupons((details.isAcceptPlatformCoupons as boolean) || false);
          }
          const plansResponse = await getPlans({ staffId: stylistId });
          setExistingPlans((plansResponse.data || []) as ExistingPlan[]);
          console.log('QuickListingWizard: Stylist data fetched.');
        } catch (fetchError) {
          console.error('Error fetching stylist data in wizard:', fetchError);
          setError(t('quickListingWizard.errorFetchingStylistData'));
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (open && stylistId) {
      fetchStylistData();
    }
  }, [open, stylistId, t, subServiceTypesOptions]); // `t` for translations in error messages

  // Effect to pre-populate plan creation form if existing plans are available
  useEffect(() => {
    if (activeStep === 2 && existingPlans.length > 0) {
      const firstPlan = existingPlans[0];
      console.log('Pre-populating plan form with:', firstPlan);
      setPlanName(firstPlan.name || '');
      setPlanZhName(firstPlan.zh_name || '');
      setPlanPrice(firstPlan.price || '');
      setPlanDuration(firstPlan.duration || '');
      setPlanDescription(firstPlan.description || '');
      setPlanZhDescription(firstPlan.zh_description || '');

      setSelectedServiceTypeId(firstPlan.serviceTypeId || '');
      setSelectedSubServiceTypeId(firstPlan.subServiceTypeId || ''); // This should now work

    } else if (activeStep === 2 && existingPlans.length === 0) {
      // Clear success message if no plans to pre-populate when entering step 2
      setSuccess('');
    }
  }, [activeStep, existingPlans, t]);

  // Load service types for plan creation
  const fetchServiceTypes = async (): Promise<void> => {
    try {
      const typesResponse = await getServiceTypes();
      setServiceTypes((Array.isArray(typesResponse) ? typesResponse : []) as ServiceType[]);
    } catch (error) {
      console.error('Error fetching service types:', error);
    }
  };

  const fetchSubServiceTypes = async (): Promise<void> => {
    try {
      const typesResponse = await getSubServiceTypes();
      setSubServiceTypesOptions((typesResponse.subServiceTypes || []) as SubServiceType[]);
    } catch (error) {
      console.error('Error fetching sub-service types:', error);
    }
  };

  const fetchAreaLocations = async (): Promise<void> => {
    try {
      const locationsResponse = await getAreaLocations();
      setAreaLocations(locationsResponse.locations || []);
    } catch (error) {
      console.error('Error fetching area locations:', error);
    }
  };

  // New handler for user-driven service type changes
  const handleUserSelectedServiceTypeChange = (newServiceTypeId: string): void => {
    setSelectedServiceTypeId(newServiceTypeId);
    setSelectedSubServiceTypeId(''); // Explicitly reset sub-service type
  };

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      setAvatarFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handlePlaceSelected = useCallback((placeData: PlaceData): void => {
    // placeData structure: { address, coordinates, place_id }
    // coordinates is an array [lng, lat] for GeoJSON format
    if (placeData.coordinates) {
      setCoordinates(placeData.coordinates);
    }
    // Store the place_id for generating Google Maps links
    if (placeData.place_id) {
      setPlaceId(placeData.place_id);
    }
  }, []);

  const handleNext = (): void => {
    if (validateCurrentStep()) {
      if (activeStep === 0) {
        handleCreateStylist();
      } else if (activeStep === 1) {
        handleUpdateStylistProfile();
      } else if (activeStep === 2) {
        handleCreatePlan();
      } else if (activeStep === 3) {
        handleCheckShowcases();
      } else if (activeStep === 4) {
        handleFinalize();
      } else {
        setActiveStep((prevStep) => prevStep + 1);
      }
    }
  };

  const handleBack = (): void => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateCurrentStep = (): boolean => {
    setError('');

    if (activeStep === 0) {
      if (!useBusinessEmail && !stylistEmail) {
        setError(t('quickListingWizard.emailRequired'));
        return false;
      }
    } else if (activeStep === 1) {
      if (!displayName) {
        setError(t('quickListingWizard.displayNameRequired'));
        return false;
      }
      if (yearOfExperience <= 0 || isNaN(parseInt(String(yearOfExperience)))) {
        setError(t('quickListingWizard.invalidYearOfExperience'));
        return false;
      }
      if (!avatarFile && !previewUrl) {
        setError(t('quickListingWizard.profilePhotoRequired'));
        return false;
      }
      if (subServiceTypes.length === 0) {
        setError(t('common.fieldRequired'));
        return false;
      }
      if (useBusinessAddress === false && (!businessAddress || !businessArea?._id)) {
        setError(t('quickListingWizard.addressAndAreaRequired'));
        return false;
      }
    } else if (activeStep === 2) {
      if (!planName || !planZhName || !planPrice || !planDuration) {
        setError(t('quickListingWizard.planDetailsRequired'));
        return false;
      }
      if (!selectedServiceTypeId || !selectedSubServiceTypeId) {
        setError(t('quickListingWizard.serviceTypeRequired'));
        return false;
      }
      if (Number(planDuration) % 15 !== 0) {
        setError(t('common.invalidDuration'));
        return false;
      }
    }

    return true;
  };

  const handleCreateStylist = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      if (useBusinessEmail) {
        // Create stylist with same email as business
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const result = await createStylist({ email: user.email });
        console.log('Stylist created:', result);
        setStylistId(result.staff._id); // Store the stylist ID
        setSuccess(t('quickListingWizard.stylistCreated'));
        setActiveStep((prevStep) => prevStep + 1);
      } else {
        // For now, we'll just move to the next step
        // In a real app, you would send the invitation here
        toast.info(t('quickListingWizard.invitationSentMessage'));
        setActiveStep((prevStep) => prevStep + 1);
      }
    } catch (error) {
      console.error('Error creating stylist:', error);

      const axiosError = error as { response?: { data?: { message?: string } } };
      // Check if error is because stylist already exists
      if (axiosError.response?.data?.message === "Stylist already exists") {
        try {
          // Get the stylists list and find the one with the same email as the business
          const stylistsResponse = await getStylists();
          const stylists = stylistsResponse;
          console.log('Existing stylists:', stylists);

          if (stylists && stylists.length > 0) {
            // Assume the first stylist is the one we want
            // In a more robust implementation, you might want to filter by email or status
            const existingStylist = stylists[0];
            console.log('Selected existing stylist:', existingStylist);

            // Make sure we get the _id field correctly
            const existingId = existingStylist._id || (existingStylist as Record<string, unknown>).id as string;
            setStylistId(existingId);
            console.log('Set stylist ID to:', existingId);

            // Show success message and continue
            setSuccess(t('quickListingWizard.stylistAlreadyExists'));
            setActiveStep((prevStep) => prevStep + 1);
          } else {
            setError(t('quickListingWizard.createStylistError'));
          }
        } catch (err) {
          console.error('Error getting existing stylists:', err);
          const innerError = err as { response?: { data?: { message?: string } } };
          setError(innerError.response?.data?.message || t('quickListingWizard.createStylistError'));
        }
      } else {
        setError(axiosError.response?.data?.message || t('quickListingWizard.createStylistError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStylistProfile = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      console.log('Updating stylist profile with ID:', stylistId);

      // Prepare the profile data
      const profileData: Record<string, FormDataValue> = {
        displayName,
        yearOfExperience: Number(yearOfExperience),
        canCommunicateInEnglish,
        description,
        zh_description,
        subServiceTypes: subServiceTypes.map(s => s._id),
        useBusinessAddress,
        businessAddress,
        businessArea: businessArea?._id ?? null,
        coordinates,
        availability: {
          dayoff: dayOff.map(Number),
          startHour,
          endHour,
          leaveDates: [] as string[],
        }
      };

      // Add the profile photo if available
      if (avatarFile) {
        profileData.avatarFile = avatarFile;
      } else if (previewUrl && previewUrl.includes(import.meta.env.VITE_R2_SALON_ENDPOINT)) {
        // If no new file but there's an existing avatar, keep the avatarKey
        const avatarKey = previewUrl.split('/').pop();
        profileData.avatarKey = avatarKey;
      }

      // Always pass the _id field if we have it
      if (stylistId) {
        profileData._id = stylistId;
        // Also include stylistId for API compatibility
        profileData.stylistId = stylistId;
      }

      console.log('Sending profile data:', profileData);

      // Update the stylist profile
      const result = await updateStylistDetails(profileData);
      console.log('Update profile result:', result);

      // Show success message
      setSuccess(t('quickListingWizard.profileUpdated'));

      // Move to the next step
      setActiveStep((prevStep) => prevStep + 1);
    } catch (error) {
      console.error('Error updating stylist profile:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || t('quickListingWizard.updateProfileError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePlan = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      const planData = {
        name: planName,
        zh_name: planZhName,
        price: planPrice,
        currency: 'HKD',
        duration: planDuration,
        description: planDescription,
        zh_description: planZhDescription,
        serviceTypeId: selectedServiceTypeId,
        subServiceTypeId: selectedSubServiceTypeId
      };

      console.log('Creating plan with data:', planData);
      const response = await createPlan(planData);
      console.log('Plan created:', response);

      // Get stylist details to read current assignedPlans
      let currentAssignedPlans: string[] = [];
      try {
        // Get current stylist details - the API will handle authentication
        const stylistDetailsResponse = await getStylistDetails(stylistId!);
        const details = stylistDetailsResponse;
        console.log('Fetched stylist details:', details);
        currentAssignedPlans = (Array.isArray(details.assignedPlans) ? details.assignedPlans : []) as string[];
      } catch (error) {
        console.error('Error getting stylist details:', error);
      }

      const assignedPlans = [...currentAssignedPlans, response.data._id];

      // Prepare update data with ID fields
      const updateData: Record<string, FormDataValue> = {
        assignedPlans,
      };

      // Always include _id if we have it
      if (stylistId) {
        updateData._id = stylistId;
        updateData.stylistId = stylistId;
      }

      console.log('Updating stylist with assigned plans:', updateData);

      // Update with stylist ID if available
      await updateStylistDetails(updateData);

      setSuccess(t('quickListingWizard.planCreated'));
      setActiveStep((prevStep) => prevStep + 1);
    } catch (error) {
      console.error('Error creating plan:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || t('quickListingWizard.createPlanError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowcaseCountChange = (count: number): void => {
    setShowcaseCount(count);
  };

  const handleCheckShowcases = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      // We already know the showcase count from our ShowcaseCreationSection component
      if (showcaseCount >= 2) {
        setSuccess(t('quickListingWizard.showcaseCountSufficient'));
      } else {
        setError(t('quickListingWizard.needMoreShowcases'));
        return; // Don't proceed if not enough showcases
      }
      setActiveStep((prevStep) => prevStep + 1);
    } catch (error) {
      console.error('Error checking showcases:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || t('quickListingWizard.checkShowcasesError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    console.log('Starting handleFinalize, current activeStep:', activeStep);

    try {
      // Update Business Profile with Reservation Mode
      console.log('Updating business profile with reservation mode:', reservationMode);
      await updateStylistDetails({
        _id: stylistId ?? undefined,
        reservationMode,
        isAcceptPlatformCoupons
      } as Record<string, FormDataValue>);

      // Update quickSetupWizardDone flag
      await updateUserFlag('quickSetupWizardDone', true);

      // Start listing the stylist
      if (stylistId) {
        const staffData = { staffId: stylistId, status: 'listing' };
        console.log('Updating staff status with data:', staffData);
        await updateStaffStatus(staffData);
      }

      // Start listing the business status (might be redundant if updateBusinessProfile covers it, but often separate)
      if (!isStylistRole) {
        console.log('Updating business status to listing');
        await updateBusinessStatus({ status: 'listing' });
      }

      // Clean up wizard state
      cleanupWizardState();

      setActiveStep(5);

    } catch (error) {
      console.error('Error finalizing listing:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || t('quickListingWizard.finalizingError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (): void => {
    // Clean up wizard state
    cleanupWizardState();

    fetchUserData(); // Call fetchUserData when the dialog is being closed from Acknowledgment step
    onClose();     // Then call the original onClose to close the dialog
  };

  const getStepContent = (step: number): React.ReactNode => {
    console.log('getStepContent called with step:', step);

    switch (step) {
      case 0:
        return (
          <StylistCreationStep
            useBusinessEmail={useBusinessEmail}
            setUseBusinessEmail={setUseBusinessEmail}
            stylistEmail={stylistEmail}
            setStylistEmail={setStylistEmail}
            isLoading={isLoading}
          />
        );

      case 1:
        return (
          <StylistProfileStep
            displayName={displayName}
            setDisplayName={setDisplayName}
            yearOfExperience={yearOfExperience}
            setYearOfExperience={(value: string) => setYearOfExperience(Number(value) || 0)}
            canCommunicateInEnglish={canCommunicateInEnglish}
            setCanCommunicateInEnglish={setCanCommunicateInEnglish}
            description={description}
            setDescription={setDescription}
            zh_description={zh_description}
            setZhDescription={setZhDescription}
            previewUrl={previewUrl}
            handleAvatarSelect={handleAvatarSelect}
            dayOff={dayOff}
            setDayOff={setDayOff}
            startHour={startHour}
            setStartHour={setStartHour}
            endHour={endHour}
            setEndHour={setEndHour}
            subServiceTypes={subServiceTypes}
            setSubServiceTypes={(value) => setSubServiceTypes(value as SubServiceType[])}
            subServiceTypesOptions={subServiceTypesOptions}
            isLoading={isLoading}
            useBusinessAddress={useBusinessAddress}
            setUseBusinessAddress={setUseBusinessAddress}
            businessAddress={businessAddress}
            onInputChange={setBusinessAddress}
            businessArea={businessArea}
            setBusinessArea={(value) => setBusinessArea(value as AreaLocation | null)}
            areaLocations={areaLocations}
            onPlaceSelected={handlePlaceSelected}
            placeId={placeId}
            GoogleMapsLinks={GoogleMapsLinks}
          />
        );

      case 2:
        return (
          <PlanCreationStep
            planName={planName}
            setPlanName={setPlanName}
            planZhName={planZhName}
            setPlanZhName={setPlanZhName}
            planPrice={planPrice}
            setPlanPrice={setPlanPrice}
            planDuration={planDuration}
            setPlanDuration={setPlanDuration}
            planDescription={planDescription}
            setPlanDescription={setPlanDescription}
            planZhDescription={planZhDescription}
            setPlanZhDescription={setPlanZhDescription}
            serviceTypes={serviceTypes}
            selectedServiceTypeId={selectedServiceTypeId}
            onServiceTypeUserChange={handleUserSelectedServiceTypeChange}
            selectedSubServiceTypeId={selectedSubServiceTypeId}
            setSelectedSubServiceTypeId={setSelectedSubServiceTypeId}
            isLoading={isLoading}
          />
        );

      case 3:
        return (
          <ShowcaseStep
            stylistId={stylistId ?? undefined}
            onShowcaseCountChange={handleShowcaseCountChange}
            isLoading={isLoading}
          />
        );

      case 4:
        return (
          <Box sx={{ mt: 2 }}>
            <FormControl component="fieldset" margin="normal" sx={{ mt: 3, mb: 1 }}>
              <FormLabel component="legend">{t('quickListingWizard.reservationModeLabel')}</FormLabel>
              <RadioGroup
                aria-label="reservation-mode"
                name="reservationMode"
                value={reservationMode}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReservationMode(e.target.value)}
              >
                <FormControlLabel
                  value="auto-confirm"
                  control={<Radio />}
                  label={t('quickListingWizard.reservationModeAutoConfirm')}
                />
                <FormControlLabel
                  value="need-approval"
                  control={<Radio />}
                  label={t('quickListingWizard.reservationModeNeedApproval')}
                />
              </RadioGroup>
            </FormControl>
            <Typography variant="caption" display="block" gutterBottom sx={{mb:2}}>
                {t('quickListingWizard.reservationModeHelpText')}
            </Typography>

            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('stylistProfileSettings.platformCouponsTooltip')}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAcceptPlatformCoupons}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIsAcceptPlatformCoupons(e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={t('stylistProfileSettings.acceptPlatformCoupons')}
              />
            </Box>

            <Typography variant="h6" gutterBottom>
              {t('quickListingWizard.finalize')}
            </Typography>

            <Typography>
              {t('quickListingWizard.finalizeInstructions')}
            </Typography>

            <Alert severity="info" sx={{ mt: 2 }}>
              {t('quickListingWizard.listingReadyMessage')}
            </Alert>
          </Box>
        );

      case 5:
        return (
          <AcknowledgmentStep
            isLoading={isLoading}
            showcaseCount={showcaseCount}
          />
        );

      default:
        return 'Unknown step';
    }
  };

  const steps: string[] = [
    t('quickListingWizard.step1'),
    t('quickListingWizard.step2'),
    t('quickListingWizard.step3'),
    t('quickListingWizard.step4'),
    t('quickListingWizard.step5'),
    t('quickListingWizard.step6')
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {t('quickListingWizard.title')}
      </DialogTitle>

      <DialogContent>
        {/* Debug button to test jumping to acknowledgment step */}
        {/* <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            onClick={() => {
              console.log('Debug: Jumping to acknowledgment step');
              setActiveStep(5);
            }}
          >
            Debug: Jump to Acknowledgment
          </Button>
        </Box> */}

        {/* Current step indicator */}
        {/* <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
          Current step: {activeStep} (Expected content: {
            activeStep === 0 ? 'Create Stylist' :
            activeStep === 1 ? 'Stylist Profile' :
            activeStep === 2 ? 'Create Plan' :
            activeStep === 3 ? 'Create Showcases' :
            activeStep === 4 ? 'Finalize Listing & Reservation Mode' :
            activeStep === 5 ? 'Acknowledgment' : 'Unknown'
          })
        </Typography> */}

        <Box sx={{ overflowX: 'auto', pb: 1 }}> {/* Wrapper for horizontal scrolling */}
          <Stepper
            activeStep={activeStep}
            sx={{
              pt: 1,  // Optional: Adjust as needed to ensure lines appear before scrolling
              ...(isMobile && {
                '& .MuiStepConnector-line': {
                  minHeight: '5px',
                  height: '5px'
                }
              })
            }}
            orientation={isMobile ? "vertical" : "horizontal"}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {t('common.cancel')}
        </Button>

        {activeStep > (isStylistRole || initialStylistId ? 1 : 0) && activeStep < steps.length - 1 && (
          <Button onClick={handleBack} disabled={isLoading}>
            {t('common.back')}
          </Button>
        )}

        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleClose}
          >
            {t('common.close')}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={24} />
            ) : activeStep === steps.length - 2 ? (
              t('common.finish')
            ) : (
              t('common.next')
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuickListingWizard;
